/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, September 16th 2022, 11:12:24 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { logger } from "lib/logger";
import { ProcessDefinition } from "lib/schemas";
import { formatBytes } from "lib/utils";
import { StyledOSDialog, StyledOSDialogProps, Image, OSActionButton, OSFileInput } from "ui-widgets";
import { InstallationButtonWrapper } from "./InstallationButtonWrapper";

export const InstallFromFile = () => {
  const [uploadedFiles, setUploadedFiles] = React.useState<File[]>();

  const installationFile = uploadedFiles?.[0];

  const isValid = installationFile?.type === "application/json";

  const [serviceTitle, setServiceTitle] = React.useState<string | null>(null);
  const readerRef = React.useRef(new FileReader());

  React.useEffect(() => {
    if (installationFile) {
      readerRef.current?.readAsText(installationFile, "UTF-8");
      readerRef.current.onload = () => {
        try {
          const objects: { model: string; fields: { [key: string]: string } }[] = JSON.parse(
            readerRef.current?.result.toString()
          );
          const processDefinitionData = objects.find((ob) => ob.model === "onestopflow.processdefinition")
            ?.fields as unknown as ProcessDefinition;
          setServiceTitle(processDefinitionData?.title);
        } catch (error) {
          logger.error("Error reading file:", error);
        }
      };
    }
  }, [installationFile]);

  return (
    <Stack spacing={2}>
      <OSFileInput
        dropTargetText="Drag a valid Onestop JSON installation file from your computer and drop it here"
        filePreviewProps={{
          validFileMessage: (
            <>
              <p>{installationFile?.name}</p>
              <Typography variant="subtitle1">
                {formatBytes(installationFile?.size)} | {installationFile?.type}
              </Typography>
            </>
          ),
          validFileIcon: <Image src="/img/config-file.png" width={80} height={80} />,
          invalidFileMessage: <p>{installationFile?.name} is not a valid installation file.</p>,
        }}
        isValid={isValid}
        uploadedFiles={uploadedFiles}
        setUploadedFiles={setUploadedFiles}
      />
      <InstallationButtonWrapper
        isAlreadyInstalled={false}
        serviceTitle={serviceTitle || installationFile?.name || "Unknown"}
        installationFile={installationFile}
      >
        {({ handleInstall }) => (
          <OSActionButton
            fullWidth
            onClick={handleInstall}
            variant="contained"
            size="large"
            style={{ marginLeft: "auto" }}
            disabled={!isValid}
          >
            Install
          </OSActionButton>
        )}
      </InstallationButtonWrapper>
    </Stack>
  );
};

export const InstallFromFileDialog = ({
  renderOpenButton,
}: Required<Pick<StyledOSDialogProps, "renderOpenButton">>) => (
  <StyledOSDialog
    open={false}
    renderOpenButton={renderOpenButton}
    popUpTitle={<>Install Service From JSON File</>}
    popUpIcon={<CloudUploadIcon color="primary" />}
  >
    <InstallFromFile />
  </StyledOSDialog>
);
